import React, { useRef, useState, useEffect, useCallback } from "react";

// Two-pane layout with a mouse-draggable divider (desktop). Stacks vertically on small screens.
export default function ResizableSplit({ children, storageKey = "split-pct", minPct = 28, maxPct = 72 }) {
  const [left, right] = React.Children.toArray(children);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const [pct, setPct] = useState(() => {
    const v = parseFloat(localStorage.getItem(storageKey));
    return Number.isFinite(v) && v >= minPct && v <= maxPct ? v : 50;
  });
  const ref = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const h = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const onMove = useCallback(
    (e) => {
      if (!dragging.current || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      let p = ((clientX - rect.left) / rect.width) * 100;
      p = Math.min(maxPct, Math.max(minPct, p));
      setPct(p);
    },
    [minPct, maxPct]
  );

  const stop = useCallback(() => {
    if (dragging.current) {
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", stop);
    };
  }, [onMove, stop]);

  useEffect(() => {
    localStorage.setItem(storageKey, String(pct));
  }, [pct, storageKey]);

  const start = () => {
    dragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  if (!isDesktop) {
    return (
      <div className="space-y-6">
        <div>{left}</div>
        <div>{right}</div>
      </div>
    );
  }

  return (
    <div ref={ref} className="flex w-full items-start" data-testid="resizable-split">
      <div style={{ width: `${pct}%` }} className="min-w-0">{left}</div>
      <div
        onMouseDown={start}
        onTouchStart={start}
        onDoubleClick={() => setPct(50)}
        className="w-3 self-stretch cursor-col-resize flex-shrink-0 group flex items-center justify-center"
        title="Drag to resize — double-click to reset"
        data-testid="split-divider"
      >
        <div className="w-1 min-h-[6rem] h-full max-h-40 rounded-full bg-slate-300 group-hover:bg-orange-400 transition-colors" />
      </div>
      <div style={{ width: `calc(${100 - pct}% - 0.75rem)` }} className="min-w-0">{right}</div>
    </div>
  );
}
